<script setup lang="ts">
/**
 * IGNITE-84: bonus Flybuys points roundel shown on plan tiles during the
 * September bonus Flybuys campaign.
 *
 * Each campaign value has its own artwork exported from the V2 Figma frame, so
 * the roundel is pixel-identical to the design - including the numerals, which
 * are set in the flybuys brand face and are not reproducible with a web font.
 *
 * Only the three campaign values have artwork. If the backend ever sends a
 * value we have no asset for, the component renders nothing rather than
 * guessing: a missing roundel is better than a wrong-looking one, and the page
 * carries on as if the field were absent.
 */
interface Props {
  /** Raw bonus points value, e.g. 1000 / 1500 / 2000 */
  points: number | string;
}

const props = defineProps<Props>();

const ROUNDEL_BY_POINTS: Record<number, string> = {
  1000: "/images/flybuys-roundel-1000.svg",
  1500: "/images/flybuys-roundel-1500.svg",
  2000: "/images/flybuys-roundel-2000.svg",
};

const roundelSrc = computed(() => {
  const value = Number(props.points);
  const src = ROUNDEL_BY_POINTS[value];
  if (!src) {
    console.warn(
      `[FlybuysRoundel] No artwork for ${props.points} bonus points - roundel hidden`
    );
  }
  return src ?? null;
});

const formattedPoints = computed(() =>
  Number(props.points).toLocaleString("en-AU")
);
</script>

<template>
  <img
    v-if="roundelSrc"
    class="flybuys-roundel"
    :src="roundelSrc"
    :alt="`${formattedPoints} bonus Flybuys points`"
    width="120"
    height="120"
  />
</template>

<style scoped lang="scss">
.flybuys-roundel {
  /* Figma draws this 120px wide on a 305px card, with the price block ending at
     174px (24px card padding + 150px for the widest price, "$12.50") - just 1px
     of clearance. The price never scales, so on a narrower card a plain
     percentage still runs into it. Sizing off the space the price leaves keeps
     the clearance at every width: 100% - 174px price - 10px right gap.
     305px card -> 121px, capped at the design's 120px. */
  width: clamp(72px, calc(100% - 184px), 120px);
  height: auto;
  aspect-ratio: 1 / 1;
  pointer-events: none;
}
</style>
